import { size } from "lodash";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { toastr } from "react-redux-toastr";
import { Box, Typography, Button, Grid } from "@mui/material";
import { IconButton, Tabs, Tab, useMediaQuery } from "@mui/material";
// component
import { InputField, LabelField } from "components";
import { PhonePicker } from "components/PhonePicker";
import FieldErrorMessage from "components/FormikError";
import { getDateFormatForDatePicket } from "helper/dateFormat";
import { convertUTCToLocalDate } from "components/UTC/UtcConverter";
import { convertLocalToUTCDate } from "components/UTC/UtcConverter";
import { ReactComponent as Edit } from "assets/icons/EditReport.svg";
import { ReactComponent as Success } from "assets/icons/PaidIcon.svg";
import { ReactComponent as Delete } from "assets/icons/DeleteBlue.svg";
// direct import
import { emailRegex } from "utils/validation";
import { MyCheckboxRenewal } from "../checkbox";
import { Fonts, FontColors } from "theme/fontConfig";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`member-tabpanel-${index}`}
      aria-labelledby={`member-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `member-tab-${index}`,
    "aria-controls": `member-tabpanel-${index}`,
  };
}

const MembersValidation = ({ familyList = [], formik }) => {
  const isSmallScreen = useMediaQuery("(max-width:750px)");

  const dateFormat = getDateFormatForDatePicket();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempMember, setTempMember] = useState(null);
  const [addedEdit, setAddedEdit] = useState(false);

  const handleTabChange = (_, newIndex) => {
    if (editingIndex !== null) {
      return;
    }
    setActiveTabIndex(newIndex);
    setEditingIndex(null);
    setTempMember(null);
  };

  const handleAddFamily = () => {
    const familyMembers = formik.values.accountInfo.family_members || [];

    const newMemberIndex = familyMembers.findIndex(
      (member) => member.isNew === true
    );

    if (newMemberIndex !== -1) {
      const member = familyMembers[newMemberIndex];
      const requiredFields = ["first_name", "last_name"];

      const missingFields = requiredFields.filter(
        (field) =>
          !member[field] ||
          (typeof member[field] === "string" && member[field].trim() === "")
      );

      if (missingFields.length > 0) {
        toastr.error(
          "Error",
          "Please fill in all required fields before adding a new family member."
        );
        return;
      }
    }

    const newMember = {
      _id: Date.now().toString(),
      first_name: "",
      last_name: "",
      email_address: "",
      mobile_number: "",
      isNew: true,
      anniversary_date: null,
      date_of_birth: null,
      home_phone: "",
    };

    const updatedList = [
      ...(formik.values.accountInfo.family_members || []),
      newMember,
    ];

    formik.setFieldValue("accountInfo.family_members", updatedList);
    setActiveTabIndex(updatedList.length - 1);
    setAddedEdit(true);
    setTimeout(() => {
      handleEdit(updatedList.length - 1);
    }, 100);
  };

  const handleDeleteFamily = () => {
    const updatedList = formik.values.accountInfo.family_members.filter(
      (_, index) => index !== activeTabIndex
    );

    formik.setFieldValue("accountInfo.family_members", updatedList);
    setActiveTabIndex(Math.max(0, activeTabIndex - 1));
    setEditingIndex(null);
    setTempMember(null);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setTempMember({ ...formik.values.accountInfo.family_members[index] });
  };

  const handleCancel = () => {
    const updatedList = [...(formik.values.accountInfo.family_members || [])];
    if (addedEdit) {
      updatedList.splice(activeTabIndex, 1);
      formik.setFieldValue("accountInfo.family_members", updatedList);
      setAddedEdit(false);
      setActiveTabIndex(0);
    } else {
      updatedList[activeTabIndex] = tempMember;
      formik.setFieldValue("accountInfo.family_members", updatedList);
    }

    setEditingIndex(null);
    setTempMember(null);
  };

  const handleUpdate = () => {
    const memberPath = `accountInfo.family_members[${activeTabIndex}]`;
    const member = formik.values.accountInfo.family_members[activeTabIndex];

    const errors = {};

    if (!member.first_name || member.first_name.trim() === "") {
      errors.first_name = "First name is required";
    }
    if (!member.last_name || member.last_name.trim() === "") {
      errors.last_name = "Last name is required";
    }

    if (member.email_address && member.email_address.trim().length > 0) {
      if (!emailRegex.test(member.email_address.trim())) {
        errors.email_address = "Invalid email address format";
      }
    }

    if (Object.keys(errors).length > 0) {
      // Set touched & errors
      Object.keys(errors).forEach((key) => {
        formik.setFieldTouched(`${memberPath}.${key}`, true, false);
        formik.setFieldError(`${memberPath}.${key}`, errors[key]);
      });
      return;
    }

    // Clear previous errors
    formik.setFieldError(`${memberPath}.first_name`, "");
    formik.setFieldError(`${memberPath}.last_name`, "");
    formik.setFieldError(`${memberPath}.email_address`, "");

    setEditingIndex(null);
    setTempMember(null);
    setAddedEdit(false);
  };

  const handleApprove = (index) => {
    const updatedAddresses = [...(familyList || [])];
    updatedAddresses[index] = {
      ...updatedAddresses[index],
      approve: true,
      edit: true,
    };
    formik.setFieldValue("accountInfo.family_members", updatedAddresses);
    setEditingIndex(null);
    if (familyList?.length - 1 !== index) setActiveTabIndex(index + 1);
  };

  return (
    <Box className="summary-renewal__card account-info">
      <Box
        sx={{
          display: "flex",
          alignItems: isSmallScreen ? "normal" : "baseline",
          justifyContent: "space-between",
          flexDirection: isSmallScreen ? "column" : "row",
        }}
      >
        <Typography variant="h6" className="title_address_details">
          Family Members Validation
        </Typography>

        <Button
          color="primary"
          onClick={handleAddFamily}
          disabled={editingIndex !== null}
          style={{
            opacity: editingIndex !== null ? 0.5 : 1,
            marginBottom: isSmallScreen ? 15 : 0,
          }}
        >
          <Box className="account-info-labels">+ Add Family Member</Box>
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="family members tabs"
        >
          {familyList?.map((member, index) => (
            <Tab
              key={member._id}
              label={
                `${
                  member?.first_name === "" && member?.isNew
                    ? "New Contact"
                    : member?.first_name
                } ${member?.last_name ?? ""}` || "New Contact"
              }
              {...a11yProps(index)}
              sx={{
                textTransform: "none",
                minWidth: "15rem",
                fontWeight: Fonts.GeneralTabFont.fontWeight,
                fontSize: Fonts.GeneralTabFont.fontSize,
                color: FontColors.Slate1F2937,
              }}
            />
          ))}
        </Tabs>
      </Box>

      {familyList?.map((member, index) => (
        <CustomTabPanel key={member._id} value={activeTabIndex} index={index}>
          <Box sx={{ mt: 2, position: "relative" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  {editingIndex !== index && (
                    <IconButton
                      onClick={() => handleEdit(index)}
                      style={{ width: 38, height: 38 }}
                    >
                      <Edit />
                    </IconButton>
                  )}

                  {!member?.approve && editingIndex !== index && (
                    <>
                      |
                      <Button
                        color="primary"
                        onClick={() => handleApprove(index)}
                      >
                        <Box className="account-info-labels">Approve</Box>
                      </Button>
                    </>
                  )}
                  {member?.approve && (
                    <IconButton>
                      <Box className="account-info-labels">
                        <Success style={{ height: 25, width: 25 }} />
                      </Box>
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <>
                <Grid item xs={12} md={6}>
                  <InputField
                    formik={formik}
                    title="First Name"
                    name={`accountInfo.family_members[${index}].first_name`}
                    placeholder="First Name"
                    value={member?.first_name || ""}
                    formStyle={{ marginBottom: 0 }}
                    disabled={editingIndex !== index}
                    mandatory
                  />
                  <FieldErrorMessage
                    formik={formik}
                    name={`accountInfo.family_members[${index}].first_name`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputField
                    formik={formik}
                    title="Last Name"
                    name={`accountInfo.family_members[${index}].last_name`}
                    placeholder="Last Name"
                    value={member?.last_name || ""}
                    formStyle={{ marginBottom: 0 }}
                    disabled={editingIndex !== index}
                    mandatory
                  />
                  <FieldErrorMessage
                    formik={formik}
                    name={`accountInfo.family_members[${index}].last_name`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputField
                    formik={formik}
                    title="Email"
                    name={`accountInfo.family_members[${index}].email_address`}
                    placeholder="Email"
                    value={member?.email_address || ""}
                    formStyle={{ marginBottom: 0 }}
                    disabled={editingIndex !== index}
                  />
                  <FieldErrorMessage
                    formik={formik}
                    name={`accountInfo.family_members[${index}].email_address`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PhonePicker
                    value={member?.mobile_number || ""}
                    handleChange={(e) => {
                      formik.setFieldValue(
                        `accountInfo.family_members[${index}].mobile_number`,
                        e
                      );
                    }}
                    placeholder="Mobile Phone"
                    title="Mobile Phone"
                    disabled={editingIndex !== index}
                  />
                  <FieldErrorMessage
                    formik={formik}
                    name={`accountInfo.family_members[${index}].mobile_number`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PhonePicker
                    value={member?.home_phone || ""}
                    handleChange={(e) => {
                      formik.setFieldValue(
                        `accountInfo.family_members[${index}].home_phone`,
                        e
                      );
                    }}
                    placeholder="Home Number"
                    title="Home Number"
                    disabled={editingIndex !== index}
                  />
                  <FieldErrorMessage
                    formik={formik}
                    name={`accountInfo.family_members[${index}].home_phone`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="date-picker-member">
                    <LabelField title="Date of birth" />
                    <DatePicker
                      isClearable
                      showIcon
                      placeholderText="Date of birth"
                      dateFormat={dateFormat}
                      selected={convertUTCToLocalDate(member?.date_of_birth)}
                      onChange={(e) => {
                        convertLocalToUTCDate(
                          e,
                          `accountInfo.family_members[${index}].date_of_birth`,
                          formik
                        );
                      }}
                      disabled={editingIndex !== index}
                    />
                    <FieldErrorMessage
                      formik={formik}
                      name={`accountInfo.family_members[${index}].date_of_birth`}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className="date-picker-member">
                    <LabelField title="Anniversary" />
                    <DatePicker
                      isClearable
                      showIcon
                      placeholderText="Anniversary"
                      dateFormat={dateFormat}
                      selected={convertUTCToLocalDate(member?.anniversary_date)}
                      onChange={(e) => {
                        convertLocalToUTCDate(
                          e,
                          `accountInfo.family_members[${index}].anniversary_date`,
                          formik
                        );
                      }}
                      disabled={editingIndex !== index}
                    />
                    <FieldErrorMessage
                      formik={formik}
                      name={`accountInfo.family_members[${index}].anniversary_date`}
                    />
                  </Box>
                </Grid>
              </>
              {!member?.isNew && (
                <Grid item xs={12} md={6}>
                  <label
                    htmlFor="honor"
                    className="d-flex align-items-center h-100 checkbox__label"
                    style={{ fontWeight: Fonts.GeneralLabelFont.fontWeight, fontSize: Fonts.GeneralLabelFont.fontSize, opacity: 0.6 }}
                  >
                    <MyCheckboxRenewal
                      checked={member.is_admin_user}
                      disabled
                    />
                    &nbsp;&nbsp; Admin
                  </label>
                </Grid>
              )}
              <Grid item xs={member?.isNew ? 12 : 6}>
                {!member?.approve && (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems={"center"}
                  >
                    {member?.isNew &&
                      size(formik?.values?.accountInfo?.family_members) > 1 &&
                      editingIndex !== index && (
                        <IconButton onClick={handleDeleteFamily}>
                          <Delete />
                        </IconButton>
                      )}
                  </Box>
                )}

                {editingIndex === index && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Button onClick={handleCancel}>
                      <Box className="account-info-labels">Cancel</Box>
                    </Button>
                    |
                    <Button onClick={handleUpdate}>
                      <Box className="account-info-labels">Update</Box>
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </CustomTabPanel>
      ))}
    </Box>
  );
};

export default MembersValidation;
